import { useState } from 'react';
import { motion } from 'framer-motion';
import { Play, X, ChevronLeft, ChevronRight, Phone, ArrowLeft, Target, Zap, HeartHandshake } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

const WHATSAPP_NUMBER = '551151250926';
const WHATSAPP_MESSAGE = 'Olá, vim pelo site de vocês. Quero fazer um orçamento para....';
const YOUTUBE_VIDEO_ID = 'XtT5qcCw3oo';

// Gallery images
const galleryImages = [
  { id: 1, src: '/gallery/obras.jpeg', alt: 'Projeto Industrial 1', category: 'industrial' },
  { id: 2, src: '/gallery/obras2.jpeg', alt: 'Projeto Industrial 2', category: 'industrial' },
  { id: 3, src: '/gallery/obras3.jpeg', alt: 'Projeto Industrial 3', category: 'revestimento' },
  { id: 4, src: '/gallery/obras4.jpeg', alt: 'Projeto Industrial 4', category: 'revestimento' },
  { id: 5, src: '/gallery/obras5.jpeg', alt: 'Projeto Industrial 5', category: 'estrutural' },
  { id: 6, src: '/gallery/obras6.jpeg', alt: 'Projeto Industrial 6', category: 'estrutural' },
  { id: 7, src: '/gallery/obras7.jpeg', alt: 'Projeto Industrial 7', category: 'industrial' },
  { id: 8, src: '/gallery/obras8.png', alt: 'Projeto Industrial 8', category: 'revestimento' },
  { id: 9, src: '/gallery/obras9.jpeg', alt: 'Projeto Industrial 9', category: 'estrutural' },
  { id: 10, src: '/gallery/obras10.jpeg', alt: 'Projeto Industrial 10', category: 'industrial' },
];

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { duration: 0.6, ease: [0.4, 0, 0.2, 1] } 
  },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.1 },
  },
};

// Lightbox Component
const Lightbox = ({ images, currentIndex, onClose, onNext, onPrev }) => {
  if (currentIndex === null) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 text-white/80 hover:text-white p-2 z-50"
        data-testid="lightbox-close"
      >
        <X className="w-8 h-8" />
      </button>
      
      <button
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        className="absolute left-4 text-white/80 hover:text-white p-2 z-50"
        data-testid="lightbox-prev"
      >
        <ChevronLeft className="w-10 h-10" />
      </button>
      
      <button
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        className="absolute right-4 text-white/80 hover:text-white p-2 z-50"
        data-testid="lightbox-next"
      >
        <ChevronRight className="w-10 h-10" />
      </button>
      
      <img
        src={images[currentIndex].src}
        alt={images[currentIndex].alt}
        className="max-h-[85vh] max-w-[90vw] object-contain"
        onClick={(e) => e.stopPropagation()}
      />
      
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/70 text-sm">
        {currentIndex + 1} / {images.length}
      </div>
    </motion.div>
  );
};

export const About = () => {
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  
  // Responsivo: 1 slide no mobile, 2 no tablet, 3 no desktop
  const slidesPerView = typeof window !== 'undefined' && window.innerWidth < 768 ? 1 : window.innerWidth < 1024 ? 2 : 3;

  const handleWhatsAppClick = () => {
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(WHATSAPP_MESSAGE)}`, '_blank');
  };

  const openLightbox = (index) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);
  const nextImage = () => setLightboxIndex((prev) => (prev + 1) % galleryImages.length);
  const prevImage = () => setLightboxIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length);
  
  // Carousel navigation
  const nextSlide = () => {
    setCurrentSlide((prev) => 
      prev + slidesPerView >= galleryImages.length ? 0 : prev + 1
    );
  };
  
  const prevSlide = () => {
    setCurrentSlide((prev) => 
      prev === 0 ? Math.max(0, galleryImages.length - slidesPerView) : prev - 1
    );
  };

  return (
    <main data-testid="about-page" className="pt-20">
      {/* Hero Section */}
      <section className="bg-slate-900 py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors"
            data-testid="back-home-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar para Home
          </Link>
          
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-center max-w-4xl mx-auto"
          >
            <motion.span 
              variants={fadeInUp}
              className="text-amber-400 text-xs font-bold uppercase tracking-[0.2em] mb-4 block"
            >
              Conheça Nossa História
            </motion.span>
            <motion.h1 
              variants={fadeInUp}
              className="font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold text-white uppercase tracking-tight mb-6"
            >
              Conheça a<br />
              <span className="text-amber-400">Ematech</span>
            </motion.h1>
            <motion.p 
              variants={fadeInUp}
              className="text-slate-300 text-base md:text-lg max-w-2xl mx-auto leading-relaxed"
            >
              Fundada em 1982, somos referência em soluções industriais com mais de 40 anos de história.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Nossa História Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={staggerContainer}
          >
            <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              <motion.div variants={fadeInUp}>
                <span className="text-amber-600 text-xs font-bold uppercase tracking-[0.2em] mb-4 block">
                  Nossa Trajetória
                </span>
                <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 uppercase tracking-tight mb-6">
                  Desde 1982<br />
                  <span className="text-slate-500">Construindo Excelência</span>
                </h2>
                <div className="space-y-4 text-slate-600 leading-relaxed">
                  <p>
                    A <span className="font-semibold text-slate-900">Revestimentos e Adesivos Ematech</span> nasceu em 1982, com raízes profundas na construção civil e um compromisso inabalável com a qualidade.
                  </p>
                  <p>
                    Com <span className="font-semibold text-slate-900">mais de 20 anos em aplicações</span> e <span className="font-semibold text-slate-900">mais de 10 anos desenvolvendo materiais próprios</span>, unimos conhecimento técnico, experiência prática e inovação contínua.
                  </p>
                  <p>
                    Nosso diferencial está em manipular diretamente a partir da matéria-prima — epóxi e poliuretano — e também executar a aplicação (mão de obra). Isso nos torna mais capacitados e nos permite oferecer <span className="font-semibold text-slate-900">preços abaixo do comum</span> sem abrir mão da qualidade.
                  </p>
                </div>
              </motion.div>
              
              <motion.div variants={fadeInUp} className="grid grid-cols-2 gap-4">
                <div className="bg-slate-900 p-6 rounded-xl text-center">
                  <div className="font-heading text-4xl md:text-5xl font-extrabold text-amber-400 mb-2">1982</div>
                  <div className="text-slate-400 text-sm">Ano de Fundação</div>
                </div>
                <div className="bg-slate-900 p-6 rounded-xl text-center">
                  <div className="font-heading text-4xl md:text-5xl font-extrabold text-amber-400 mb-2">+20</div>
                  <div className="text-slate-400 text-sm">Anos em Aplicações</div>
                </div>
                <div className="bg-slate-900 p-6 rounded-xl text-center">
                  <div className="font-heading text-4xl md:text-5xl font-extrabold text-amber-400 mb-2">+10</div>
                  <div className="text-slate-400 text-sm">Anos Desenvolvendo Materiais</div>
                </div>
                <div className="bg-slate-900 p-6 rounded-xl text-center">
                  <div className="font-heading text-4xl md:text-5xl font-extrabold text-amber-400 mb-2">BR</div>
                  <div className="text-slate-400 text-sm">Atendimento Nacional</div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Valores Section */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="text-center max-w-3xl mx-auto mb-12">
              <span className="text-amber-600 text-xs font-bold uppercase tracking-[0.2em] mb-4 block">
                O Que Nos Move
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 uppercase tracking-tight mb-6">
                Nossos Valores
              </h2>
            </motion.div>

            <div className="grid md:grid-cols-3 gap-8">
              <motion.div variants={fadeInUp} className="bg-white p-8 rounded-2xl border border-slate-200 text-center group hover:shadow-xl hover:border-amber-200 transition-all duration-300">
                <div className="w-20 h-20 bg-gradient-to-br from-amber-400 to-amber-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-amber-500/30 group-hover:scale-110 transition-transform duration-300">
                  <Target className="w-10 h-10 text-white" strokeWidth={2} />
                </div>
                <h3 className="font-heading text-xl font-bold text-slate-900 uppercase mb-3">Excelência</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Buscamos a perfeição em cada projeto, usando materiais de alta qualidade e técnicas refinadas ao longo de décadas.
                </p>
              </motion.div>

              <motion.div variants={fadeInUp} className="bg-white p-8 rounded-2xl border border-slate-200 text-center group hover:shadow-xl hover:border-amber-200 transition-all duration-300">
                <div className="w-20 h-20 bg-gradient-to-br from-slate-700 to-slate-900 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-slate-500/30 group-hover:scale-110 transition-transform duration-300">
                  <Zap className="w-10 h-10 text-amber-400" strokeWidth={2} />
                </div>
                <h3 className="font-heading text-xl font-bold text-slate-900 uppercase mb-3">Agilidade</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Entendemos a urgência do seu projeto. Por isso, trabalhamos com rapidez sem comprometer a qualidade do resultado final.
                </p>
              </motion.div>

              <motion.div variants={fadeInUp} className="bg-white p-8 rounded-2xl border border-slate-200 text-center group hover:shadow-xl hover:border-amber-200 transition-all duration-300">
                <div className="w-20 h-20 bg-gradient-to-br from-amber-400 to-amber-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-amber-500/30 group-hover:scale-110 transition-transform duration-300">
                  <HeartHandshake className="w-10 h-10 text-white" strokeWidth={2} />
                </div>
                <h3 className="font-heading text-xl font-bold text-slate-900 uppercase mb-3">Empatia</h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  Ouvimos nossos clientes e entendemos suas necessidades. Cada projeto é único e tratamos com atenção personalizada.
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="text-center max-w-3xl mx-auto mb-12">
              <span className="text-amber-600 text-xs font-bold uppercase tracking-[0.2em] mb-4 block">
                Vídeo Institucional
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 uppercase tracking-tight mb-6">
                Nossa Essência
              </h2>
              <p className="text-slate-600 text-base md:text-lg leading-relaxed">
                Assista ao vídeo e conheça um pouco mais sobre quem somos e como trabalhamos.
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="max-w-4xl mx-auto">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-slate-900">
                <div className="aspect-video">
                  <iframe
                    src={`https://www.youtube.com/embed/${YOUTUBE_VIDEO_ID}?rel=0&modestbranding=1`}
                    title="Conheça a Ematech"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="w-full h-full"
                    data-testid="video-embed"
                  />
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Gallery Section - Carrossel */}
      <section className="py-16 md:py-24 bg-slate-50 overflow-hidden">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="text-center max-w-3xl mx-auto mb-12">
              <span className="text-amber-600 text-xs font-bold uppercase tracking-[0.2em] mb-4 block">
                Portfólio
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-slate-900 uppercase tracking-tight mb-6">
                Projetos Realizados
              </h2>
              <p className="text-slate-600 text-base md:text-lg leading-relaxed">
                Confira alguns dos nossos trabalhos em revestimentos, pinturas industriais e reforços estruturais.
              </p>
            </motion.div>

            {/* Carousel */}
            <motion.div variants={fadeInUp} className="relative">
              {/* Navigation Buttons */}
              <button
                onClick={prevSlide}
                className="absolute left-0 md:-left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white shadow-lg rounded-full flex items-center justify-center hover:bg-slate-50 transition-colors"
                data-testid="carousel-prev"
              >
                <ChevronLeft className="w-6 h-6 text-slate-700" />
              </button>
              
              <button
                onClick={nextSlide}
                className="absolute right-0 md:-right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white shadow-lg rounded-full flex items-center justify-center hover:bg-slate-50 transition-colors"
                data-testid="carousel-next"
              >
                <ChevronRight className="w-6 h-6 text-slate-700" />
              </button>
              
              {/* Carousel Container */}
              <div className="overflow-hidden mx-8 md:mx-12">
                <motion.div 
                  className="flex gap-4 md:gap-6"
                  animate={{ x: `-${currentSlide * (100 / slidesPerView)}%` }}
                  transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                >
                  {galleryImages.map((image, index) => (
                    <div
                      key={image.id}
                      className="flex-shrink-0 w-full md:w-1/2 lg:w-1/3"
                      style={{ minWidth: `calc(${100 / slidesPerView}% - 1rem)` }}
                    >
                      <div 
                        className="relative aspect-[4/3] rounded-2xl overflow-hidden cursor-pointer group shadow-lg"
                        onClick={() => openLightbox(index)}
                        data-testid={`gallery-image-${image.id}`}
                      >
                        <img
                          src={image.src}
                          alt={image.alt}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-6">
                          <span className="text-white font-semibold text-sm bg-amber-500 px-4 py-2 rounded-full">
                            Ver Projeto
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </motion.div>
              </div>
              
              {/* Dots Indicator */}
              <div className="flex justify-center gap-2 mt-8">
                {Array.from({ length: Math.ceil(galleryImages.length / slidesPerView) }).map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentSlide(idx * slidesPerView)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      Math.floor(currentSlide / slidesPerView) === idx 
                        ? 'bg-amber-500 w-8' 
                        : 'bg-slate-300 hover:bg-slate-400'
                    }`}
                    data-testid={`carousel-dot-${idx}`}
                  />
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-slate-900">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center max-w-3xl mx-auto"
          >
            <motion.h2 
              variants={fadeInUp}
              className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-white uppercase tracking-tight mb-6"
            >
              Gostou do que viu?
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-slate-400 text-base md:text-lg mb-10 leading-relaxed"
            >
              Entre em contato e solicite um orçamento para o seu projeto. Atendemos todo o Brasil.
            </motion.p>
            
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                data-testid="about-cta-whatsapp"
                onClick={handleWhatsAppClick}
                className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold px-8 py-6 text-base rounded-lg shadow-lg hover:shadow-xl flex items-center justify-center gap-3 uppercase tracking-widest transition-all duration-200"
              >
                <Phone className="w-5 h-5" />
                Falar no WhatsApp
              </Button>
              
              <Link to="/produtos">
                <Button
                  data-testid="about-cta-products"
                  variant="outline"
                  className="w-full sm:w-auto bg-transparent border-white/25 text-white hover:bg-white/10 hover:border-white/40 font-semibold px-8 py-6 text-base rounded-lg flex items-center justify-center gap-3 transition-all duration-200"
                >
                  Ver Produtos
                </Button>
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <Lightbox
          images={galleryImages}
          currentIndex={lightboxIndex}
          onClose={closeLightbox}
          onNext={nextImage}
          onPrev={prevImage}
        />
      )}
    </main>
  );
};

export default About;
